import {
  addMonths,
  compareAsc,
  differenceInMonths,
  format,
  isValid,
  parseISO,
  startOfDay,
} from 'date-fns';
import { LEGAL_MODEL_ASSUMPTIONS } from './legalAssumptions';
import type {
  AdverseEventReason,
  CalculationTrace,
  DemoContract,
  EquityBps,
  EquitySummary,
  Milestone,
  MilestoneCalculation,
  MilestoneStatus,
  Scenario,
  ScenarioCalculation,
} from './types';
import { STORAGE_SCHEMA_VERSION } from './types';

const MAJOR_EVENT_REASONS = new Set<AdverseEventReason>([
  'major_breach',
  'confidentiality_breach',
  'undisclosed_conflict',
  'non_compete_breach',
  'competing_project',
]);

const ORDINARY_EVENT_REASONS = new Set<AdverseEventReason>([
  'ordinary_termination',
  'service_stopped',
]);

type RepurchaseMode = 'none' | 'ordinary' | 'nominal';

export function createDefaultScenario(contract: DemoContract): Scenario {
  const scenario = structuredClone(contract.scenario);
  scenario.schemaVersion = STORAGE_SCHEMA_VERSION;
  return normalizeScenario(scenario);
}

export function normalizeScenario(scenario: Scenario): Scenario {
  const normalized = structuredClone(scenario);
  normalized.schemaVersion = STORAGE_SCHEMA_VERSION;
  normalized.exercisedBpsByMilestone = { ...normalized.exercisedBpsByMilestone };
  normalized.milestones = normalized.milestones
    .map((milestone) => normalizeMilestoneForGate(milestone, normalized))
    .sort((a, b) => a.displayOrder - b.displayOrder);

  for (const milestone of normalized.milestones) {
    const current = normalized.exercisedBpsByMilestone[milestone.id] ?? 0;
    const vested = calculateVestingBps(milestone.equityBps, milestone.achievedAt, normalized.asOfDate);
    normalized.exercisedBpsByMilestone[milestone.id] = clampExercisedBps(current, vested);
  }

  return normalized;
}

export function calculateScenario(scenario: Scenario): ScenarioCalculation {
  const normalized = normalizeScenario(scenario);
  const milestones = normalized.milestones.map((milestone) => calculateMilestone(normalized, milestone));
  const summary = milestones.reduce<EquitySummary>(
    (acc, item) => ({
      potentialBps: roundBps(acc.potentialBps + item.potentialBps),
      milestoneEarnedBps: roundBps(acc.milestoneEarnedBps + item.milestoneEarnedBps),
      timeVestedBps: roundBps(acc.timeVestedBps + item.timeVestedBps),
      exercisableBps: roundBps(acc.exercisableBps + item.exercisableBps),
      exercisedBps: roundBps(acc.exercisedBps + item.exercisedBps),
      lapsedBps: roundBps(acc.lapsedBps + item.lapsedBps),
      blockedBps: roundBps(acc.blockedBps + item.blockedBps),
      repurchaseAt80RiskBps: roundBps(
        acc.repurchaseAt80RiskBps + item.repurchaseAt80RiskBps,
      ),
      repurchaseAtNominalRiskBps: roundBps(
        acc.repurchaseAtNominalRiskBps + item.repurchaseAtNominalRiskBps,
      ),
    }),
    createEmptySummary(),
  );

  return {
    scenario: normalized,
    summary,
    milestones,
    assumptions: LEGAL_MODEL_ASSUMPTIONS,
  };
}

export function calculateMilestone(scenario: Scenario, milestoneInput: Milestone): MilestoneCalculation {
  const milestone = normalizeMilestoneForGate(milestoneInput, scenario);
  const potentialBps = milestone.equityBps;
  const blocked = !scenario.conflictCleared;
  const achievedDate = parseDate(milestone.achievedAt);
  const asOfDate = parseDate(scenario.asOfDate) ?? startOfDay(new Date());
  const isFutureAchievement =
    Boolean(achievedDate) && compareAsc(achievedDate as Date, asOfDate) === 1;

  if (blocked) {
    const calculation = buildMilestoneCalculation({
      milestone,
      status: 'blocked',
      potentialBps,
      milestoneEarnedBps: 0,
      timeVestedBps: 0,
      exercisableBps: 0,
      exercisedBps: 0,
      lapsedBps: 0,
      blockedBps: potentialBps,
      repurchaseAt80RiskBps: 0,
      repurchaseAtNominalRiskBps: 0,
      vestingPercent: 0,
      monthsElapsed: 0,
    });
    return { ...calculation, trace: buildCalculationTrace(scenario, calculation) };
  }

  const milestoneEarnedBps =
    milestone.achieved && !isFutureAchievement ? milestone.equityBps : 0;
  const monthsElapsed =
    achievedDate && !isFutureAchievement
      ? Math.max(0, differenceInMonths(asOfDate, achievedDate))
      : 0;
  const vestingPercent = getVestingPercent(monthsElapsed);
  const timeVestedBps =
    milestone.achieved && milestone.achievedAt && !isFutureAchievement
      ? calculateVestingBps(milestone.equityBps, milestone.achievedAt, scenario.asOfDate)
      : 0;
  const exercisedBps = clampExercisedBps(
    scenario.exercisedBpsByMilestone[milestone.id] ?? 0,
    timeVestedBps,
  );
  const repurchaseMode = getRepurchaseMode(scenario);
  const unexercisedBps = Math.max(0, milestone.equityBps - exercisedBps);
  const lapsedBps = repurchaseMode === 'none' ? 0 : roundBps(unexercisedBps);
  const repurchaseAt80RiskBps =
    repurchaseMode === 'ordinary' ? roundBps(exercisedBps) : 0;
  const repurchaseAtNominalRiskBps =
    repurchaseMode === 'nominal' ? roundBps(exercisedBps) : 0;
  const exercisableBps =
    repurchaseMode === 'none' ? roundBps(Math.max(0, timeVestedBps - exercisedBps)) : 0;

  const status = resolveStatus({
    milestone,
    isFutureAchievement,
    milestoneEarnedBps,
    timeVestedBps,
    exercisableBps,
    exercisedBps,
    lapsedBps,
    repurchaseAt80RiskBps,
    repurchaseAtNominalRiskBps,
  });

  const calculation = buildMilestoneCalculation({
    milestone,
    status,
    potentialBps,
    milestoneEarnedBps,
    timeVestedBps,
    exercisableBps,
    exercisedBps,
    lapsedBps,
    blockedBps: 0,
    repurchaseAt80RiskBps,
    repurchaseAtNominalRiskBps,
    vestingPercent,
    monthsElapsed,
  });

  return { ...calculation, trace: buildCalculationTrace(scenario, calculation) };
}

export function calculateVestingBps(
  equityBps: EquityBps,
  achievedAt: string | null,
  asOfDate: string,
): EquityBps {
  const achievedDate = parseDate(achievedAt);
  const currentDate = parseDate(asOfDate);

  if (!achievedDate || !currentDate || compareAsc(currentDate, achievedDate) < 0) {
    return 0;
  }

  const monthsElapsed = Math.max(0, differenceInMonths(currentDate, achievedDate));
  const vestingPercent = getVestingPercent(monthsElapsed);
  return roundBps((equityBps * vestingPercent) / 100);
}

export function clampExercisedBps(exercisedBps: EquityBps, timeVestedBps: EquityBps): EquityBps {
  if (!Number.isFinite(exercisedBps) || exercisedBps < 0) {
    return 0;
  }

  return roundBps(Math.min(exercisedBps, Math.max(0, timeVestedBps)));
}

export function buildCalculationTrace(
  scenario: Scenario,
  calculation: MilestoneCalculation,
): CalculationTrace {
  const steps: string[] = [];
  const milestone = calculation.milestone;

  if (!scenario.conflictCleared) {
    steps.push('第3.3条前置限制尚未清理。');
    steps.push('其他里程碑输入可以保存，但不得计入乙方可主张的有效孵化权益/认股权。');
    return {
      milestoneId: milestone.id,
      heading: `${milestone.shortTitle}计算路径`,
      steps,
    };
  }

  steps.push(
    scenario.conflictClearedAt
      ? `第3.3条前置限制已于 ${scenario.conflictClearedAt} 完成清理。`
      : '已标记前置限制清理完成，但尚未填写完成日期。',
  );

  if (milestone.achieved && milestone.achievedAt) {
    steps.push(`${milestone.id} 于 ${milestone.achievedAt} 达成里程碑成熟条件。`);
    steps.push(`截至 ${scenario.asOfDate}，已经过 ${calculation.monthsElapsed} 个完整月份。`);
    steps.push(`按第1.4条，该里程碑当前完成 ${calculation.vestingPercent}% 时间归属。`);
    steps.push(`当前已完成时间归属：${formatBps(calculation.timeVestedBps)}。`);
  } else if (milestone.achieved) {
    steps.push(`${milestone.id} 已标记达成，但尚未填写达成日期。`);
    steps.push('模型记录里程碑成熟事实，但不计算第1.4条时间归属。');
  } else {
    steps.push(`${milestone.id} 尚未达成里程碑成熟条件。`);
  }

  if (calculation.exercisableBps > 0) {
    steps.push(`当前可行权：${formatBps(calculation.exercisableBps)}。`);
  }

  if (calculation.exercisedBps > 0) {
    steps.push(`模拟中已行权/已实际取得：${formatBps(calculation.exercisedBps)}。`);
  }

  if (calculation.lapsedBps > 0) {
    steps.push(`根据第5.5条所选情景，已失效：${formatBps(calculation.lapsedBps)}。`);
  }

  if (calculation.repurchaseAt80RiskBps > 0) {
    steps.push(`进入上一轮融资价格/估值80%回购风险：${formatBps(calculation.repurchaseAt80RiskBps)}。`);
  }

  if (calculation.repurchaseAtNominalRiskBps > 0) {
    steps.push(
      `进入成本价或名义价格回购风险：${formatBps(calculation.repurchaseAtNominalRiskBps)}。`,
    );
  }

  return {
    milestoneId: milestone.id,
    heading: `${milestone.shortTitle}计算路径`,
    steps,
  };
}

export function formatBps(bps: EquityBps): string {
  const roundedBps = roundBps(bps);
  const percentage = roundBps(roundedBps / 100);
  return `${formatNumber(percentage)}% / ${formatNumber(roundedBps)}基点`;
}

export function formatDateLabel(date: string | null): string {
  const parsed = parseDate(date);
  return parsed ? format(parsed, 'yyyy-MM-dd') : '未设置';
}

export function milestoneAnniversary(achievedAt: string | null, months: number): string | null {
  const parsed = parseDate(achievedAt);
  return parsed ? format(addMonths(parsed, months), 'yyyy-MM-dd') : null;
}

function buildMilestoneCalculation(
  calculation: Omit<MilestoneCalculation, 'trace'>,
): MilestoneCalculation {
  return {
    ...calculation,
    trace: {
      milestoneId: calculation.milestone.id,
      heading: `${calculation.milestone.shortTitle}计算路径`,
      steps: [],
    },
  };
}

function resolveStatus(input: {
  milestone: Milestone;
  isFutureAchievement: boolean;
  milestoneEarnedBps: EquityBps;
  timeVestedBps: EquityBps;
  exercisableBps: EquityBps;
  exercisedBps: EquityBps;
  lapsedBps: EquityBps;
  repurchaseAt80RiskBps: EquityBps;
  repurchaseAtNominalRiskBps: EquityBps;
}): MilestoneStatus {
  if (input.repurchaseAtNominalRiskBps > 0) {
    return 'repurchase_nominal_risk';
  }

  if (input.repurchaseAt80RiskBps > 0) {
    return 'repurchase_80_risk';
  }

  if (input.lapsedBps > 0) {
    return 'lapsed';
  }

  if (input.isFutureAchievement) {
    return 'future';
  }

  if (input.exercisableBps > 0) {
    return 'exercisable';
  }

  if (input.exercisedBps > 0 && input.exercisedBps >= input.timeVestedBps) {
    return 'exercised';
  }

  if (input.milestoneEarnedBps > 0 && input.timeVestedBps > 0) {
    return 'vesting';
  }

  if (input.milestoneEarnedBps > 0) {
    return 'earned';
  }

  return 'pending';
}

function normalizeMilestoneForGate(milestone: Milestone, scenario: Scenario): Milestone {
  if (milestone.id !== 'M1') {
    return { ...milestone };
  }

  return {
    ...milestone,
    achieved: scenario.conflictCleared,
    achievedAt: scenario.conflictCleared ? scenario.conflictClearedAt : null,
  };
}

function getRepurchaseMode(scenario: Scenario): RepurchaseMode {
  if (MAJOR_EVENT_REASONS.has(scenario.adverseEventReason)) {
    return 'nominal';
  }

  if (
    ORDINARY_EVENT_REASONS.has(scenario.adverseEventReason) ||
    scenario.agreementTerminated ||
    !scenario.serviceActive
  ) {
    return 'ordinary';
  }

  return 'none';
}

function getVestingPercent(monthsElapsed: number): number {
  if (monthsElapsed >= 36) {
    return 100;
  }

  if (monthsElapsed >= 24) {
    return 50;
  }

  if (monthsElapsed >= 12) {
    return 25;
  }

  return 0;
}

function createEmptySummary(): EquitySummary {
  return {
    potentialBps: 0,
    milestoneEarnedBps: 0,
    timeVestedBps: 0,
    exercisableBps: 0,
    exercisedBps: 0,
    lapsedBps: 0,
    blockedBps: 0,
    repurchaseAt80RiskBps: 0,
    repurchaseAtNominalRiskBps: 0,
  };
}

function parseDate(value: string | null): Date | null {
  if (!value) {
    return null;
  }

  const parsed = startOfDay(parseISO(value));
  return isValid(parsed) ? parsed : null;
}

function roundBps(value: number): EquityBps {
  return Math.round((value + Number.EPSILON) * 1000) / 1000;
}

function formatNumber(value: number): string {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 3,
  }).format(roundBps(value));
}
